import React, { lazy, Suspense, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useOrdersQuery } from '#/api/queries/orderQueries';
import { useVaultOnChainWithdrawalQueueQuery } from '#/api/queries/vaultsOnChainQueries';
import { useWalletWithPositionsQuery } from '#/api/queries/walletPositionsQueries';
import TabsNavigation from '#/components/basic/Button/TabsNavigation';
import ToggleSlider from '#/components/basic/inputs/ToggleSlider/ToggleSlider';
import ActiveOrdersTableWrap from '#/components/orders/ActiveOrdersTableWrap';
import OrderDetailScreen from '#/components/orders/detail/OrderDetailScreen';
import PositionsTableWrap from '#/components/positions/PositionsTableWrap';
import { useIsMobileBuySellRoute } from '#/components/trading/dashboardDetect';
import MyTradeHistory from '#/components/trading/MyTrades/MyTradeHistory';
import { useOwnVaultQuery } from '#/components/vaults/shared/utils';
import { setTradeHistoryAggregateFills } from '#/reduxState/exchange/userGuideReducer';
import { toogleHideOtherMarkets } from '#/reduxState/markets/userHideOtherMarkets';
import { staticDispatch, useSelector } from '#/reduxState/store';

const VaultWithdrawalQueueTable = lazy(
  () => import('#/components/vaults/components/VaultWithdrawalQueueTable')
);

const tabs = {
  positions: 1,
  activeOrders: 2,
  tradeHistory: 3,
  /** Optional (for Vault manager wallets) */
  vaultManagerWithdrawalQueue: 4,
} as const;

type TabType = (typeof tabs)[keyof typeof tabs];

const Wrap = styled.div`
  grid-area: areaTabs;

  display: flex;
  flex-direction: column;
  /* overflow: hidden; */
  position: relative;
  padding-top: 8px;

  border-radius: 2px;
  background: var(--elevation-light);
`;

const NavWrap = styled.nav`
  font-size: var(--smaller);
  padding-bottom: 8px;
  margin: 0;
  display: flex;
`;

const TabsWrap = styled.div`
  /* width: 100%;
  height: 100%; */
  padding: 0 8px;
  /* overflow: auto; */
  /* $ {scrollbarStyle} */
  /* .isMobileStyle & { */
  /* This will allow Sticky table headers to work */
  /* overflow: visible;
  } */
`;

const TabsRightSide = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
  padding-right: 8px;
`;

const MobileTabControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  padding: 19px 0;
`;

function DashboardBottomTabs({
  isMobileTradeScreen,
}: {
  isMobileTradeScreen?: boolean;
}) {
  const ownVault = useOwnVaultQuery();

  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const isMobile = useSelector(state => state.screen.isMobile);
  const [tab, setTab] = useState<TabType>(tabs.positions);

  const isTradeRoute = useIsMobileBuySellRoute();

  const [selectedOrderFill, setSelectedOrderFill] = useState<string>();
  const [selectedOrder, setSelectedOrder] = useState<string>();

  const hideOtherMarketsFromState = useSelector(
    state => state.markets.userHideOtherMarkets
  );
  const hideOtherMarkets = isMobileTradeScreen
    ? true
    : hideOtherMarketsFromState;

  // Persisted user preference shared with the Trade History page
  const aggregateFills = useSelector(
    state => state.exchange.userGuide.tradeHistoryAggregateFills
  );

  const currentMarketPair = useSelector(
    state => state.markets.currentMarket.pair
  );

  const { data: orders } = useOrdersQuery();
  const { data: walletWithPositions } = useWalletWithPositionsQuery();

  const { data: withdrawalQueueData } = useVaultOnChainWithdrawalQueueQuery({
    managerWallet: ownVault ? ownVault.manager : undefined,
  });

  const positions = walletWithPositions?.positions;

  const marketOrdersCount = useMemo(
    () => orders?.filter(order => order.market === currentMarketPair).length,
    [orders, currentMarketPair]
  );

  const marketPositionsCount = useMemo(
    () =>
      positions?.filter(position => position.market === currentMarketPair)
        .length,
    [positions, currentMarketPair]
  );

  const handleCloseDetail = () => {
    setSelectedOrder(undefined);
    setSelectedOrderFill(undefined);
  };

  const openPositions = !positions
    ? ''
    : hideOtherMarkets && marketPositionsCount !== positions.length
    ? `${marketPositionsCount} / ${positions.length}`
    : positions.length.toString();

  const openOrdersCount = !orders
    ? ''
    : hideOtherMarkets && marketOrdersCount !== orders.length
    ? `${marketOrdersCount} / ${orders.length}`
    : orders.length.toString();

  if (isTradeRoute && !isMobileTradeScreen) {
    // Keep state (selected tabs..) but release DOM elements until back on Dashboard
    return null;
  }

  const tabsComponent = (
    <TabsNavigation
      buttons={[
        {
          title: 'Positions',
          titleBubble: openPositions,
          onClick: () => setTab(tabs.positions),
          active: tab === tabs.positions,
          stretch: isMobile,
        },
        {
          title: 'Open Orders',
          titleBubble: openOrdersCount,
          onClick: () => setTab(tabs.activeOrders),
          active: tab === tabs.activeOrders,
          stretch: isMobile,
        },
        {
          title: 'Trade History',
          onClick: () => setTab(tabs.tradeHistory),
          active: tab === tabs.tradeHistory,
          stretch: isMobile,
        },
        ...(ownVault
          ? [
              {
                title: 'Withdrawal Queue',
                titleBubble: withdrawalQueueData
                  ? `${withdrawalQueueData.length}`
                  : '',
                onClick: () => setTab(tabs.vaultManagerWithdrawalQueue),
                active: tab === tabs.vaultManagerWithdrawalQueue,
                stretch: isMobile,
              },
            ]
          : []),
      ]}
    />
  );

  const hideAllMarketsComponent = isMobileTradeScreen ? undefined : (
    <ToggleSlider
      checked={hideOtherMarkets}
      onChange={hide => {
        staticDispatch(toogleHideOtherMarkets(hide));
      }}
      disabled={!walletAddress}
    >
      Hide Other Markets
    </ToggleSlider>
  );

  const aggregateFillsComponent =
    tab === tabs.tradeHistory ? (
      <ToggleSlider
        checked={aggregateFills}
        onChange={aggregate => {
          staticDispatch(setTradeHistoryAggregateFills(aggregate));
        }}
        disabled={!walletAddress}
      >
        Aggregate
      </ToggleSlider>
    ) : undefined;

  return selectedOrder && isMobile ? (
    <OrderDetailScreen
      orderUuid={selectedOrder}
      highlightedFillId={selectedOrderFill}
      showingOn="dashboard"
      onClose={handleCloseDetail}
    />
  ) : (
    <Wrap>
      {isMobile ? (
        tabsComponent
      ) : (
        <NavWrap>
          {tabsComponent}
          {(aggregateFillsComponent || hideAllMarketsComponent) && (
            <TabsRightSide>
              {aggregateFillsComponent}
              {hideAllMarketsComponent}
            </TabsRightSide>
          )}
        </NavWrap>
      )}
      {selectedOrder && !isMobile && (
        <OrderDetailScreen
          orderUuid={selectedOrder}
          highlightedFillId={selectedOrderFill}
          showingOn="dashboard"
          onClose={handleCloseDetail}
        />
      )}

      <TabsWrap>
        {tab === tabs.positions ? (
          <PositionsTableWrap
            marketFilter={hideOtherMarkets ? currentMarketPair : undefined}
            mobileLeftTopBlock={hideAllMarketsComponent}
          />
        ) : tab === tabs.activeOrders ? (
          <ActiveOrdersTableWrap
            marketFilter={hideOtherMarkets ? currentMarketPair : undefined}
            onSelectOrder={setSelectedOrder}
            mobileLeftTopBlock={hideAllMarketsComponent}
          />
        ) : tab === tabs.tradeHistory ? (
          <>
            {isMobile && (
              <MobileTabControls>
                {aggregateFillsComponent}
                {hideAllMarketsComponent}
              </MobileTabControls>
            )}
            <MyTradeHistory
              marketFilter={hideOtherMarkets ? currentMarketPair : undefined}
              onSelectOrder={(orderId, highlightFillId) => {
                setSelectedOrder(orderId);
                setSelectedOrderFill(highlightFillId);
              }}
            />
          </>
        ) : tab === tabs.vaultManagerWithdrawalQueue ? (
          <Suspense>
            <VaultWithdrawalQueueTable managerWallet={walletAddress} />
          </Suspense>
        ) : null}
      </TabsWrap>
    </Wrap>
  );
}

export default DashboardBottomTabs;
